import { useState, useEffect } from 'react'
import { PartnerService, PartnerServiceZod } from '@duck/api-definitions'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@duck/ui'
import { Input } from '@duck/ui'
import { Textarea } from '@duck/ui'
import { Button } from '@duck/ui'
import { MarkdownEditor } from '@duck/ui'
import { IconTooltip } from '@duck/ui'
import { apis } from '@/modules/apiClients'
import { useAuth } from '@/hooks/useAuth'
import { useCohorts } from '@/hooks/useCohorts'
import { usePartnerTar, PartnerTranslationKey } from '@/hooks/usePartnerTar'
import { useTar } from '@/modules'
import { useNavigate } from '@tanstack/react-router'
import { cn } from '@duck/ui'

interface CreateCohortDialogProps {
  isOpen: boolean
  onClose: () => void
  cohort?: PartnerService.Cohort | null
  onCohortUpdated?: () => void
}

export const CreateCohortDialog = ({ isOpen, onClose, cohort, onCohortUpdated }: CreateCohortDialogProps) => {
  const { t } = useTar()
  const { t: tP } = usePartnerTar()
  const { organizationType, organizationId } = useAuth()
  const { refetch } = useCohorts()
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [challengeDisclaimer, setChallengeDisclaimer] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<{
    name?: string
    description?: string
    challengeDisclaimer?: string
  }>({})

  const isEditMode = !!cohort

  // Initialize form with cohort data when in edit mode
  useEffect(() => {
    if (!isOpen) {
      return
    }
    if (cohort) {
      setName(cohort.name || '')
      setDescription(cohort.description || '')
      setChallengeDisclaimer(cohort.challengeDisclaimer || '')
    } else {
      setName('')
      setDescription('')
      setChallengeDisclaimer('')
    }
  }, [isOpen, cohort])

  // Map organizationType to CohortType
  const getDefaultCohortType = (): PartnerService.CohortType => {
    if (organizationType === 'school') {
      return PartnerService.CohortType.Class
    }
    return PartnerService.CohortType.Position
  }

  const handleClose = () => {
    if (!isSubmitting) {
      setName('')
      setDescription('')
      setChallengeDisclaimer('')
      setError(null)
      setFieldErrors({})
      onClose()
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setFieldErrors({})

    if (isEditMode && cohort && organizationId) {
      // Update existing cohort
      const requestData: PartnerService.UpdateCohortRequest = {
        name: name.trim(),
        ...(description.trim() && { description: description.trim() }),
        ...(challengeDisclaimer.trim() && { challengeDisclaimer: challengeDisclaimer.trim() }),
        organizationId,
        type: cohort.type,
        isActive: cohort.isActive,
        personIds: cohort.personIds,
      }

      // Validate using zod schema
      try {
        PartnerServiceZod.schemas.UpdateCohortRequest.parse(requestData)
      } catch (validationError: unknown) {
        if (validationError instanceof z.ZodError) {
          const newFieldErrors: { name?: string; description?: string; challengeDisclaimer?: string } = {}

          validationError.errors.forEach((err) => {
            const fieldPath = err.path[0]
            if (fieldPath === 'name') {
              newFieldErrors.name = err.message
            } else if (fieldPath === 'description') {
              newFieldErrors.description = err.message
            } else if (fieldPath === 'challengeDisclaimer') {
              newFieldErrors.challengeDisclaimer = err.message
            }
          })

          setFieldErrors(newFieldErrors)
          return
        }
        setError('Validation failed. Please check your input.')
        return
      }

      try {
        setIsSubmitting(true)
        await apis.partnerService.cohortsCohortIdPut(cohort.id, requestData)

        // Refetch cohorts to update the list
        await refetch()

        // Call the update callback if provided
        onCohortUpdated?.()

        // Close dialog and reset form
        handleClose()
      } catch (err: unknown) {
        console.error('Failed to update cohort:', err)
        const errorMessage = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
          || 'Failed to update cohort. Please try again.'
        setError(errorMessage)
      } finally {
        setIsSubmitting(false)
      }
    } else {
      // Create new cohort
      const requestData: PartnerService.CreateCohortRequest = {
        name: name.trim(),
        ...(description.trim() && { description: description.trim() }),
        ...(challengeDisclaimer.trim() && { challengeDisclaimer: challengeDisclaimer.trim() }),
        type: getDefaultCohortType(),
        isActive: true,
      }

      // Validate using zod schema
      try {
        PartnerServiceZod.schemas.CreateCohortRequest.parse(requestData)
      } catch (validationError: unknown) {
        if (validationError instanceof z.ZodError) {
          const newFieldErrors: { name?: string; description?: string; challengeDisclaimer?: string } = {}

          validationError.errors.forEach((err) => {
            const fieldPath = err.path[0]
            if (fieldPath === 'name') {
              newFieldErrors.name = err.message
            } else if (fieldPath === 'description') {
              newFieldErrors.description = err.message
            } else if (fieldPath === 'challengeDisclaimer') {
              newFieldErrors.challengeDisclaimer = err.message
            }
          })

          setFieldErrors(newFieldErrors)
          return
        }
        setError('Validation failed. Please check your input.')
        return
      }

      try {
        setIsSubmitting(true)
        const response = await apis.partnerService.cohortsPost(requestData)

        // Get the created cohort ID
        const id = response.data.data.id

        // Refetch cohorts to update the list
        await refetch()

        // Navigate to the new cohort page
        navigate({ to: '/partner/c/$id', params: { id } })

        // Close dialog and reset form
        handleClose()
      } catch (err: unknown) {
        console.error('Failed to create cohort:', err)
        const errorMessage = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
          || 'Failed to create cohort. Please try again.'
        setError(errorMessage)
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !isSubmitting && !open && handleClose()}
    >
      <DialogContent
        onPointerDownOutside={(e) => isSubmitting && e.preventDefault()}
        onEscapeKeyDown={(e) => isSubmitting && e.preventDefault()}
        onInteractOutside={(e) => isSubmitting && e.preventDefault()}
        width="md"
      >
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? tP('editDetails') : tP('newCohort')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="cohort-name" className="block text-sm font-medium text-foreground">
              {t('common.name')}
              <span className="text-error ml-1">*</span>
            </label>
            <Input
              id="cohort-name"
              type="text"
              autoComplete="off"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isSubmitting}
              placeholder={t('common.enterValue', { value: t('common.name').toLowerCase() })}
              className={fieldErrors.name ? 'border-error' : ''}
            />
            {fieldErrors.name && (
              <p className="text-sm text-error">{fieldErrors.name}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="cohort-description" className="block text-sm font-medium text-foreground">
              {t('common.description')}
            </label>
            <Textarea
              id="cohort-description"
              autoComplete="off"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isSubmitting}
              placeholder={t('common.enterValue', { value: t('common.description').toLowerCase() })}
              className={`min-h-[100px] resize-y ${fieldErrors.description ? 'border-error' : ''}`}
            />
            {fieldErrors.description && (
              <p className="text-sm text-error">{fieldErrors.description}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="cohort-challenge-disclaimer" className="flex items-center gap-2 text-sm font-medium text-foreground">
              {t('challenges.challengeUserInstructions')}
              <IconTooltip content={t('common.markdownSupportDescription')} />
            </label>
            <div className={cn('border-border rounded-md h-[200px]', fieldErrors.challengeDisclaimer ? 'border-error' : '')}>
              <MarkdownEditor
                defaultValue={challengeDisclaimer}
                onChange={(value) => setChallengeDisclaimer(value)}
                placeholder={tP('challengeUserInstructionsPlaceholder')}
                className="max-h-[300px]"
                mdClassName="bg-background"
                mode={isSubmitting ? 'view' : 'edit'}
              />
            </div>
            {fieldErrors.challengeDisclaimer && (
              <p className="text-sm text-error">{fieldErrors.challengeDisclaimer}</p>
            )}
          </div>

          {error && (
            <div className="p-3 bg-error/10 border border-error/20 rounded-md">
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {t('common.saving')}
                </>
              ) : isEditMode ? (
                t('common.save')
              ) : (
                tP('createCohort')
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
